/**
 * Client-side auth. Azure Static Web Apps exposes the signed-in user at
 * `/.auth/me`. This is for UI gating only — every API endpoint independently
 * re-verifies the role server-side from `x-ms-client-principal`.
 *
 * --------------------------------------------------------------------------
 * Why this is split into two fetches (perf — read before recombining):
 *
 * These used to be one function: `/.auth/me` awaited, then `/api/me` awaited.
 * That made the *entire* app wait on the slowest thing in the stack. `/.auth/me`
 * is served by the SWA edge in ~50ms, but `/api/me` hits the Flex Consumption
 * Function App, which scales to zero and must cold-start, and then queries the
 * `editors` table, which may have to wait out an Azure SQL serverless
 * auto-resume (up to 60s — see api/src/db/client.ts). Chaining them behind a
 * blocking `<ProtectedRoute>` splash meant a cold load showed "Loading…" for
 * a minute or more before a single screen query was even issued.
 *
 * Now the two are independent queries that run concurrently:
 *
 *   fetchPrincipal()  /.auth/me   — fast, edge-served. Gates the route.
 *   fetchRole()       /api/me     — slow, cold-start-prone. Gates edit buttons.
 *
 * The app renders as soon as the principal lands, so screen data queries start
 * in parallel with the role lookup rather than after it. Edit affordances
 * appear a moment later when the role resolves. The API enforces roles on
 * every write regardless, so a briefly-unknown role is a cosmetic delay, not
 * a security question.
 * --------------------------------------------------------------------------
 */

export interface AuthUser {
  userId: string;
  email: string;
  roles: string[];
  isEditor: boolean;
}

/** The SWA-resolved identity, before the Editor role is known. */
export interface Principal {
  userId: string;
  email: string;
  roles: string[];
}

/** The Editor-role answer from the API. */
export interface Role {
  email: string;
  isEditor: boolean;
}

interface ClientPrincipalResponse {
  clientPrincipal: {
    identityProvider: string;
    userId: string;
    userDetails: string;
    userRoles: string[];
  } | null;
}

/** SWA endpoint that begins the Entra sign-in flow. */
export const LOGIN_URL = '/.auth/login/aad?post_login_redirect_uri=/';

/** SWA endpoint that clears the session and returns to the login screen. */
export const LOGOUT_URL = '/.auth/logout?post_logout_redirect_uri=/login';

/**
 * Read the signed-in SWA principal. Edge-served, so this is the fast half of
 * auth and the one the router gates on. `null` means "not signed in".
 */
export async function fetchPrincipal(): Promise<Principal | null> {
  const res = await fetch('/.auth/me');
  if (!res.ok) return null;

  const body = (await res.json()) as ClientPrincipalResponse;
  const principal = body.clientPrincipal;
  if (!principal || !principal.userRoles.includes('authenticated')) return null;

  return {
    userId: principal.userId,
    email: principal.userDetails ?? '',
    roles: principal.userRoles,
  };
}

/**
 * Resolve the caller's Editor status from the API (the `editors` table) — SWA's
 * role mechanism is unavailable on a Flex Consumption backend.
 *
 * A failure here is NOT "the user is a Viewer" — it's "we don't know the role
 * yet". Throwing (instead of resolving with isEditor:false) makes React Query
 * retry and, crucially, keep the last known-good role during a refetch — so an
 * Editor never silently flickers to Viewer. The API still enforces roles
 * server-side on every write.
 */
export async function fetchRole(): Promise<Role> {
  let res: Response;
  try {
    res = await fetch('/api/me');
  } catch (e) {
    throw new Error(`Role lookup failed: ${(e as Error).message}`);
  }
  if (!res.ok) {
    throw new Error(`Role lookup failed (/api/me returned ${res.status})`);
  }
  const me = (await res.json()) as { email?: string; isEditor?: boolean };
  return { email: me.email ?? '', isEditor: me.isEditor === true };
}
